import { jsx } from 'react/jsx-runtime';

const publicPostfix = "__PUBLIC";
const envRef = {
  current: deserializePublicEnvironment()
};
const envOverrideRef = {
  current: {}
};
const listeners = /* @__PURE__ */ new Set();
const environment = Object.freeze({
  /**
   * reads an environment variable
   *
   * NOTE that this method will throw early if the variable hasn't been defined,
   * as we wish to enforce explicit configurations rather than having implicit
   * defaults in some cases
   */
  read(key, options) {
    const envVars = getEnvironmentVariables();
    const envPostfix = (() => {
      var _a;
      const appEnv = (_a = envVars["APP_ENV"]) == null ? void 0 : _a.toUpperCase();
      switch (appEnv) {
        case "TEST":
        case "STAGING":
        case "PRODUCTION":
          return `__${appEnv}`;
        default:
          return "__TEST";
      }
    })();
    const value = envVars[`${key}${envPostfix}`] ?? envVars[`${key}${envPostfix}${publicPostfix}`] ?? envVars[key] ?? envVars[`${key}${publicPostfix}`];
    if (value === void 0 && !(options == null ? void 0 : options.optional)) {
      throw new ReferenceError(
        `environment.read(): referencing undefined key "${key}".`
      );
    }
    return value;
  },
  /**
   * override the in-memory record of available environment variables
   */
  override(nextEnv) {
    envOverrideRef.current = {
      ...envOverrideRef.current,
      ...nextEnv
    };
    listeners.forEach((listener) => {
      listener();
    });
  },
  /**
   * enable observing of changes within the available environment variables, so
   * that other sources can listen for changes and react to them accordingly
   */
  observe(listener) {
    const uniqueListener = () => {
      listener();
    };
    listeners.add(uniqueListener);
    return () => listeners.delete(uniqueListener);
  }
});
function getEnvironmentVariables() {
  return {
    ...envRef.current ?? process.env,
    ...envOverrideRef.current
  };
}
const ALWAYS_GLOBAL_KEYS = ["NODE_ENV", "APP_ENV"];
function serializePublicEnvironment() {
  function serialize() {
    const serializedOutput = JSON.stringify(
      Object.fromEntries(
        Object.entries(getEnvironmentVariables()).filter(
          ([key]) => key.endsWith(publicPostfix) || ALWAYS_GLOBAL_KEYS.includes(key)
        ).map(([key, value]) => [key.replace(/__PUBLIC$/, ""), value])
      )
    );
    return /* @__PURE__ */ jsx(
      "script",
      {
        type: "text/json",
        "data-gyl--env": true,
        dangerouslySetInnerHTML: { __html: serializedOutput }
      }
    );
  }
  const publicEnv = {
    current: serialize()
  };
  environment.observe(() => {
    publicEnv.current = serialize();
  });
  return publicEnv;
}
function deserializePublicEnvironment() {
  var _a;
  if (typeof document === "undefined") {
    return;
  }
  const serializedEnvVars = (_a = document.querySelector(
    "script[data-gyl--env]"
  )) == null ? void 0 : _a.textContent;
  return JSON.parse(serializedEnvVars ?? "null");
}

export { deserializePublicEnvironment, environment, serializePublicEnvironment };
//# sourceMappingURL=util.environment.js.map
